import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export default function SettingsSmtpPage() {
  const { profile } = useAuth();
  const [host, setHost] = useState('');
  const [port, setPort] = useState('587');
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [encryption, setEncryption] = useState('tls');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!profile?.company_id) return;
      const { data } = await supabase
        .from('whatsapp_configs')
        .select('settings')
        .eq('company_id', profile.company_id)
        .maybeSingle();
      if (data?.settings && typeof data.settings === 'object') {
        const s = data.settings as Record<string, unknown>;
        if (typeof s.smtp_host === 'string') setHost(s.smtp_host);
        if (typeof s.smtp_port === 'string') setPort(s.smtp_port);
        if (typeof s.smtp_user === 'string') setUser(s.smtp_user);
        if (typeof s.smtp_from_name === 'string') setFromName(s.smtp_from_name);
        if (typeof s.smtp_from_email === 'string') setFromEmail(s.smtp_from_email);
        if (typeof s.smtp_encryption === 'string') setEncryption(s.smtp_encryption);
      }
      setLoaded(true);
    };
    fetchConfig();
  }, [profile?.company_id]);

  const handleSave = async () => {
    if (!profile?.company_id) return;
    setSaving(true);
    setMessage(null);
    try {
      const { data: existing } = await supabase
        .from('whatsapp_configs')
        .select('id, settings')
        .eq('company_id', profile.company_id)
        .maybeSingle();

      const currentSettings = (existing?.settings && typeof existing.settings === 'object') ? (existing.settings as Record<string, unknown>) : {};
      const newSettings = {
        ...currentSettings,
        smtp_host: host,
        smtp_port: port,
        smtp_user: user,
        smtp_password: password || currentSettings.smtp_password,
        smtp_from_name: fromName,
        smtp_from_email: fromEmail,
        smtp_encryption: encryption,
      };

      if (existing?.id) {
        await supabase.from('whatsapp_configs').update({ settings: newSettings, updated_at: new Date().toISOString() }).eq('id', existing.id);
      } else {
        await supabase.from('whatsapp_configs').insert({ company_id: profile.company_id, settings: newSettings });
      }
      setMessage({ type: 'success', text: 'Configurações SMTP salvas com sucesso!' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao salvar' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const { error } = await supabase.functions.invoke('test-whatsapp-connection', {
        body: { action: 'test_smtp', host, port, user, password, encryption, fromEmail },
      });
      if (error) throw error;
      setMessage({ type: 'success', text: 'E-mail de teste enviado! Verifique sua caixa de entrada.' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Falha no teste' });
    } finally {
      setTesting(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-heading font-bold text-foreground-950">Configuração SMTP</h2>
        <p className="text-sm text-foreground-500 mt-1">Configure o servidor de e-mail para notificações e recuperação de senha</p>
      </div>

      {message && (
        <div className={`rounded-xl px-4 py-3 flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <i className={`${message.type === 'success' ? 'ri-check-line text-emerald-500' : 'ri-error-warning-line text-red-500'} text-lg`} />
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      <div className="bg-background-100 rounded-2xl border border-background-200 p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Servidor SMTP</label>
            <input type="text" value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.gmail.com" className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Porta</label>
            <input type="text" value={port} onChange={(e) => setPort(e.target.value)} placeholder="587" className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Usuário</label>
            <input type="text" value={user} onChange={(e) => setUser(e.target.value)} placeholder="seu@email.com" className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Senha / App Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nome do Remetente</label>
            <input type="text" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="WZap PRO" className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">E-mail do Remetente</label>
            <input type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="noreply@seudominio.com" className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Criptografia</label>
            <select value={encryption} onChange={(e) => setEncryption(e.target.value)} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-all cursor-pointer">
              <option value="tls">TLS</option>
              <option value="ssl">SSL</option>
              <option value="none">Nenhuma</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3 pt-3 border-t border-background-100">
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            {saving ? <><i className="ri-loader-4-line animate-spin" /> Salvando...</> : <><i className="ri-save-line" /> Salvar</>}
          </button>
          <button onClick={handleTest} disabled={testing || !host} className="px-5 py-2 bg-secondary-500 hover:bg-secondary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            {testing ? <><i className="ri-loader-4-line animate-spin" /> Testando...</> : <><i className="ri-mail-send-line" /> Testar Envio</>}
          </button>
        </div>
      </div>
    </div>
  );
}